import {
  useRef, useState, useMemo, useCallback
} from 'react'
import {
  Stage, Text, Label, Tag, Layer, Shape, Line, Rect, Circle, Group
} from 'react-konva'
import {Box} from '@binance/uikit'
import {formatNumber} from 'utils/format'
import useDepth from 'hooks/useDepth'
import useRouter from 'hooks/useRouter'
import useSymbolInfo from 'hooks/useSymbolInfo'

const STROKE_WIDTH = 1
const GRAPH_STROKE_WIDTH = 2
const MASK_OPACITY = 0.45
const AXIS_X_HEIGHT = 22
const BG_COLOR = '#12161C'

export default function DepthChart (props) {
  const router = useRouter()
  const {symbol} = router.query

  const stageRef = useRef(null)
  const {scale} = props

  const {symbolInfo} = useSymbolInfo()

  const pricePrecision = symbolInfo?.pricePrecision

  const [cursor, setCursor] = useState({
    x: null,
    y: null
  })
  const handleMouseMove = useCallback(() => {
    setCursor(stageRef.current.getPointerPosition())
  }, [])

  const handleMouseLeave = useCallback(() => {
    setCursor({x: null, y: null})
  }, [])

  const canvasWidth = props?.size?.width
  const canvasHalfWidth = canvasWidth / 2
  const canvasHeight = props?.size?.height
  const chartHeight = canvasHeight - AXIS_X_HEIGHT

  const {
    asks,
    bids,
    pricePos,
    depthPos,
    askX,
    askY,
    bidX,
    bidY,
    currentAsk,
    currentBid,
    scaleArray
  } = useDepth(symbol, canvasWidth, chartHeight, scale, cursor, false)

  const bidColor = '#00c582'
  const askColor = '#ff0372'
  const bidFillColor = 'rgba(46, 189, 133, 0.2)'
  const askFillColor = 'rgba(242, 48, 81, 0.2)'

  return useMemo(
    () => (
      <Box display={props.hidden ? 'none' : 'block'}>
        <Stage
          ref={stageRef}
          width={canvasWidth}
          height={canvasHeight}
          onMouseMove={handleMouseMove}
          onMouseOut={handleMouseLeave}
        >
          <Layer hitGraphEnabled={false}>
            <Group height={chartHeight}>
              <Line
                points={[canvasHalfWidth, 0, canvasHalfWidth, chartHeight]}
                stroke="rgba(100, 100, 100, 0.4)"
                strokeWidth={STROKE_WIDTH}
              />
              <Shape
                sceneFunc={(context, shape) => {
                  context.beginPath()
                  let x = canvasHalfWidth
                  let y = chartHeight
                  let firstDraw = true

                  bids.forEach(({price, depth}) => {
                    x = pricePos(price)
                    if (x < 0 || x > canvasHalfWidth) return
                    const nextY = depthPos(depth)
                    if (firstDraw) {
                      context.moveTo(x, y)
                      firstDraw = false
                    } else {
                      context.lineTo(x, y)
                    }
                    y = nextY
                    context.lineTo(x, y)
                  })
                  context.lineTo(-STROKE_WIDTH, y)
                  context.lineTo(-STROKE_WIDTH, chartHeight)
                  // (!) Konva specific method, it is very important
                  context.fillStrokeShape(shape)
                }}
                fill={bidFillColor}
                stroke={bidColor}
                strokeWidth={GRAPH_STROKE_WIDTH}
              />
              <Shape
                sceneFunc={(context, shape) => {
                  context.beginPath()
                  let x = canvasHalfWidth
                  let y = chartHeight
                  let firstDraw = false

                  asks.forEach(({price, depth}) => {
                    x = pricePos(price)
                    if (x > canvasWidth || x < canvasHalfWidth) return
                    const nextY = depthPos(depth)
                    if (firstDraw) {
                      context.moveTo(x, y)
                      firstDraw = false
                    } else {
                      context.lineTo(x, y)
                    }
                    y = nextY
                    context.lineTo(x, y)
                  })
                  context.lineTo(canvasWidth + STROKE_WIDTH, y)
                  context.lineTo(canvasWidth + STROKE_WIDTH, chartHeight)
                  // (!) Konva specific method, it is very important
                  context.fillStrokeShape(shape)
                }}
                fill={askFillColor}
                stroke={askColor}
                strokeWidth={GRAPH_STROKE_WIDTH}
              />
              {askX ? (
                <>
                  <Rect
                    x={askX}
                    y={0}
                    width={canvasWidth - askX}
                    height={chartHeight}
                    fill="#000"
                    opacity={MASK_OPACITY}
                  />
                  <Rect
                    x={0}
                    y={0}
                    width={bidX}
                    height={chartHeight}
                    fill="#000"
                    opacity={MASK_OPACITY}
                  />
                  <Line
                    points={[askX, 0, askX, chartHeight]}
                    stroke={askColor}
                    strokeWidth={STROKE_WIDTH}
                    dash={[2, 2]}
                  />
                  <Line
                    points={[canvasHalfWidth, askY, canvasWidth, askY]}
                    stroke={askColor}
                    strokeWidth={STROKE_WIDTH}
                    dash={[2, 2]}
                  />
                  <Line
                    points={[bidX, 0, bidX, chartHeight]}
                    stroke={bidColor}
                    strokeWidth={STROKE_WIDTH}
                    dash={[2, 2]}
                  />
                  <Line
                    points={[0, bidY, canvasHalfWidth, bidY]}
                    stroke={bidColor}
                    strokeWidth={STROKE_WIDTH}
                    dash={[2, 2]}
                  />
                  <Circle x={askX} y={askY} radius={3} fill={askColor} />
                  <Circle x={bidX} y={bidY} radius={3} fill={bidColor} />
                  <Label x={canvasWidth} y={askY}>
                    <Tag
                      pointerDirection="right"
                      pointerWidth={9}
                      pointerHeight={18}
                      stroke={askColor}
                      strokeWidth={STROKE_WIDTH}
                      fill={BG_COLOR}
                    />
                    <Text text={currentAsk?.depth} fill={askColor} padding={3} />
                  </Label>
                  <Label x={0} y={bidY}>
                    <Tag
                      pointerDirection="left"
                      pointerWidth={9}
                      pointerHeight={18}
                      stroke={bidColor}
                      strokeWidth={STROKE_WIDTH}
                      fill={BG_COLOR}
                    />
                    <Text text={currentBid?.depth} fill={bidColor} padding={3} />
                  </Label>
                </>
              ) : null}
            </Group>
            <Group y={chartHeight}>
              {scaleArray.map((price, idx) => (
                <React.Fragment key={idx}>
                  <Line
                    points={[pricePos(price), 0, pricePos(price), 4]}
                    stroke="#fff"
                    strokeWidth={STROKE_WIDTH}
                  />
                  <Text
                    x={pricePos(price) - 20}
                    y={3}
                    text={formatNumber(price, pricePrecision)}
                    fill={'#999'}
                    padding={3}
                  />
                </React.Fragment>
              ))}
              {askX ? (
                <>
                  <Label x={askX} y={0}>
                    <Tag
                      pointerDirection="up"
                      pointerWidth={4}
                      pointerHeight={2}
                      stroke={askColor}
                      strokeWidth={STROKE_WIDTH}
                      fill={BG_COLOR}
                    />
                    <Text text={currentAsk?.price} fill={askColor} padding={3} />
                  </Label>
                  <Label x={bidX} y={0}>
                    <Tag
                      pointerDirection="up"
                      pointerWidth={4}
                      pointerHeight={2}
                      stroke={bidColor}
                      strokeWidth={STROKE_WIDTH}
                      fill={BG_COLOR}
                    />
                    <Text text={currentBid?.price} fill={bidColor} padding={3} />
                  </Label>
                </>
              ) : null}
            </Group>
          </Layer>
        </Stage>
      </Box>
    ),
    [
      props.hidden,
      canvasWidth,
      canvasHeight,
      handleMouseMove,
      handleMouseLeave,
      chartHeight,
      canvasHalfWidth,
      askX,
      bidX,
      askY,
      bidY,
      currentAsk,
      currentBid,
      scaleArray,
      bids,
      pricePos,
      depthPos,
      asks,
      pricePrecision
    ]
  )
}
